import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

// Hook pour détecter quand un élément est visible à l'écran
const useInView = (ref, threshold = 0.1, rootMargin = "0px") => {
    const [isInView, setIsInView] = useState(false);

    useEffect(() => {
        const currentRef = ref.current;
        if (!currentRef) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                // Si l'élément apparaît pour la première fois, on active l'animation
                if (entry.isIntersecting && !isInView) {
                    setIsInView(true);
                }
            },
            { threshold, rootMargin }
        );

        observer.observe(currentRef);

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [ref, threshold, rootMargin, isInView]);

    return isInView;
};

const ProjectItem = ({ project, onClick, onHover, index = 0 }) => {
    const [isMobile, setIsMobile] = useState(false);
    const projectRef = useRef(null);
    const isVisible = useInView(projectRef, 0.1, "0px");
    const navigate = useNavigate();

    // Détection mobile simplifiée
    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 768);
        checkMobile();

        window.addEventListener("resize", checkMobile);
        return () => window.removeEventListener("resize", checkMobile);
    }, []);

    const aspectRatio = isMobile ? project.ratio.mobile : project.ratio.desktop;

    // Handlers simplifiés
    const handleMouseEnter = () => onHover(project);
    const handleMouseLeave = () => onHover(null);

    // Modifié pour naviguer vers la page détaillée
    const handleClick = () => {
        // Sauvegarde de la position de défilement actuelle
        sessionStorage.setItem("portfolioScrollY", window.scrollY);

        // Appel de l'ancien handler pour maintenir la compatibilité
        if (onClick) onClick(project);

        // Navigation vers la page de détail
        navigate(`/project/${project.id}`);
    };

    useEffect(() => {
        if (!project) return;

        const styleId = `project-style-${project.id}`;
        let styleElement = document.getElementById(styleId);

        // Create the style element if it doesn't exist
        if (!styleElement) {
            styleElement = document.createElement("style");
            styleElement.id = styleId;
            document.head.appendChild(styleElement);
        }

        // Set the CSS content with mobile-first approach
        styleElement.innerHTML = `
            /* Styles de base pour mobile - toujours appliqués */
            @media (max-width: 767px) {
                .project-item-container[data-project-id="${project.id}"] .project-card {
                    background-color: ${project.color}30;
                    box-shadow: 0 8px 20px -10px rgba(0, 0, 0, 0.2);
                }
                
                .project-item-container[data-project-id="${project.id}"] .project-image {
                    transform: scale(1.05);
                    filter: saturate(1.2);
                }
                
                .project-item-container[data-project-id="${project.id}"] .shadow-effect {
                    opacity: 1;
                }
                
                .project-item-container[data-project-id="${project.id}"] .overlay {
                    opacity: 0.9;
                    background: linear-gradient(
                        to bottom,
                        transparent 30%,
                        black
                    );
                }
                
                .project-item-container[data-project-id="${project.id}"] .category-badge {
                    background-color: rgb(0 0 0 / 1);
                    color: white;
                    opacity: 0.9;
                }
                
                .project-item-container[data-project-id="${project.id}"] .content {
                    background: linear-gradient(
                        to top,
                        #000000e0,
                        transparent 90%
                    );
                }
                
                .project-item-container[data-project-id="${project.id}"] .project-title {
                    transform: translateY(0);
                }
                
                .project-item-container[data-project-id="${project.id}"] .description,
                .project-item-container[data-project-id="${project.id}"] .tags,
                .project-item-container[data-project-id="${project.id}"] .cta {
                    opacity: 1;
                    transform: translateY(0);
                }
                
                .project-item-container[data-project-id="${project.id}"] .tag-item {
                    opacity: 1;
                    transform: translateY(0);
                    background-color: white;
                    color: black;
                }
                
                .project-item-container[data-project-id="${project.id}"] .arrow-icon {
                    transform: translateX(3px);
                }
            }

            /* Styles de base pour tous les écrans */
            .project-item-container[data-project-id="${project.id}"] .project-card {
                transform: translateY(0);
                transition: transform 0.3s cubic-bezier(0.2, 0, 0.2, 1),
                    background-color 0.3s ease, box-shadow 0.3s ease;
            }
            
            /* Styles de hover pour desktop uniquement */
            @media (min-width: 768px) {
                .project-item-container[data-project-id="${project.id}"] .project-image {
                    transition: transform 0.5s cubic-bezier(0.2, 0, 0.2, 1),
                        filter 0.5s ease;
                }
                
                .project-item-container[data-project-id="${project.id}"] .shadow-effect {
                    box-shadow: 0 0 20px 0 rgba(0, 0, 0, 0.3);
                    transition: opacity 0.3s ease;
                    opacity: 0;
                }
                
                .project-item-container[data-project-id="${project.id}"] .overlay {
                    opacity: 0.6;
                    background: linear-gradient(
                        to bottom,
                        transparent,
                        rgba(0, 0, 0, 0.5)
                    );
                    transition: opacity 0.3s ease, background 0.3s ease;
                }
                
                .project-item-container[data-project-id="${project.id}"] .category-badge {
                    transition: all 0.3s ease;
                    background-color: rgba(255, 255, 255, 0.8);
                    color: #333;
                }
                
                .project-item-container[data-project-id="${project.id}"] .content {
                    background: linear-gradient(
                        to top,
                        rgba(0, 0, 0, 0.7),
                        transparent
                    );
                    transition: background 0.3s ease;
                    display: flex;
                    align-items: flex-end;
                    height: 100%;
                    padding: 0;
                }
                
                .project-item-container[data-project-id="${project.id}"] .content-wrapper {
                    padding: 1.25rem;
                    width: 100%;
                    position: relative;
                    transform: translateY(0);
                    transition: transform 0.3s cubic-bezier(0.2, 0, 0.2, 1);
                }
                
                .project-item-container[data-project-id="${project.id}"] .project-title {
                    transition: all 0.3s cubic-bezier(0.2, 0, 0.2, 1);
                    margin-bottom: 0;
                    display: block;
                }
                
                .project-item-container[data-project-id="${project.id}"] .description,
                .project-item-container[data-project-id="${project.id}"] .tags,
                .project-item-container[data-project-id="${project.id}"] .cta {
                    max-height: 0;
                    opacity: 0;
                    overflow: hidden;
                    visibility: hidden;
                    transition: all 0.3s ease;
                    margin: 0;
                }
                
                .project-item-container[data-project-id="${project.id}"] .tag-item {
                    background-color: white;
                    opacity: 0;
                    transform: translateY(10px);
                    transition: opacity 0.3s ease, transform 0.3s cubic-bezier(0.2, 0, 0.2, 1);
                    color: black;
                }
                
                .project-item-container[data-project-id="${project.id}"] .arrow-icon {
                    transform: translateX(0);
                    transition: transform 0.3s ease;
                }
                
                /* Hover effects for desktop */
                .project-item-container[data-project-id="${project.id}"]:hover .project-card {
                    transform: translateY(-8px);
                    background-color: ${project.color}30;
                    box-shadow: 0 8px 20px -10px rgba(0, 0, 0, 0.2);
                }
                
                .project-item-container[data-project-id="${project.id}"]:hover .project-image {
                    transform: scale(1.05);
                    filter: saturate(1.2);
                }
                
                .project-item-container[data-project-id="${project.id}"]:hover .shadow-effect {
                    opacity: 1;
                }
                
                .project-item-container[data-project-id="${project.id}"]:hover .overlay {
                    opacity: 0.9;
                    background: linear-gradient(
                        to bottom,
                        transparent 30%,
                        black
                    );
                }
                
                .project-item-container[data-project-id="${project.id}"]:hover .category-badge {
                    background-color: rgb(0 0 0 / 1);
                    color: white;
                    opacity: 0.9;
                }
                
                .project-item-container[data-project-id="${project.id}"]:hover .content {
                    background: linear-gradient(
                        to top,
                        #000000e0,
                        transparent 90%
                    );
                }
                
                .project-item-container[data-project-id="${project.id}"]:hover .content-wrapper {
                    transform: translateY(0);
                }
                
                .project-item-container[data-project-id="${project.id}"]:hover .project-title {
                    margin-bottom: 0.75rem;
                }
                
                .project-item-container[data-project-id="${project.id}"]:hover .description,
                .project-item-container[data-project-id="${project.id}"]:hover .tags,
                .project-item-container[data-project-id="${project.id}"]:hover .cta {
                    max-height: 200px;
                    opacity: 1;
                    visibility: visible;
                    margin-bottom: 0.75rem;
                }
                
                .project-item-container[data-project-id="${project.id}"]:hover .cta {
                    transition-delay: 0.1s;
                    margin-bottom: 0;
                }
                
                .project-item-container[data-project-id="${project.id}"]:hover .description {
                    transition-delay: 0.05s;
                }
                
                .project-item-container[data-project-id="${project.id}"]:hover .tag-item {
                    opacity: 1;
                    transform: translateY(0);
                }
                
                .project-item-container[data-project-id="${project.id}"]:hover .arrow-icon {
                    transform: translateX(3px);
                }
            }
        `;

        // Clean up when component unmounts or project changes
        return () => {
            if (styleElement && document.head.contains(styleElement)) {
                document.head.removeChild(styleElement);
            }
        };
    }, [project]);

    return (
        <button
            ref={projectRef}
            className={`project-item-container h-full cursor-pointer group text-left ${
                isVisible ? "visible" : "not-visible"
            }`}
            data-project-id={project.id}
            onClick={handleClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            style={{
                gridArea: project.gridArea,
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translateY(0)" : "translateY(30px)",
                transition: `opacity 0.6s ease-out ${
                    index * 0.1
                }s, transform 0.6s ease-out ${index * 0.1}s`,
            }}
        >
            <div className="h-full p-3">
                <div
                    className="project-card relative h-full rounded-xl overflow-hidden flex justify-center items-center"
                    style={{
                        backgroundColor: `${project.color}10`,
                    }}
                >
                    <div
                        className="w-full h-full overflow-hidden relative"
                        style={{ aspectRatio }}
                    >
                        {/* Overlay gradient */}
                        <div className="overlay absolute inset-0 z-10"></div>

                        {/* Image */}
                        <img
                            src={project.image}
                            alt={project.title}
                            className="project-image w-full h-full object-cover"
                            loading="lazy"
                        />

                        {/* Shadow effect */}
                        <div className="shadow-effect absolute inset-0 opacity-0"></div>

                        {/* Category Badge */}
                        <div className="absolute top-4 left-4 z-20">
                            <span className="category-badge inline-block px-3 py-1 text-xs font-semibold rounded-full backdrop-blur-sm bg-white bg-opacity-80 text-gray-800">
                                {project.category}
                            </span>
                        </div>

                        {/* Content */}
                        <div className="content absolute bottom-0 left-0 right-0 z-20 p-5 flex flex-col justify-end text-left">
                            <div className="content-wrapper">
                                <h3 className="project-title md:text-2xl font-heading font-bold text-white mb-2">
                                    {project.title}
                                </h3>

                                <div className="hidden-content">
                                    {/* Description */}
                                    <p
                                        className="description text-white/90 text-sm line-clamp-2 mb-2"
                                        data-nosnippet
                                    >
                                        {project.description}
                                    </p>

                                    {/* Tags */}
                                    <div className="tags flex flex-wrap gap-2 text-left">
                                        {project.tags
                                            .slice(0, 2)
                                            .map((tag, idx) => (
                                                <span
                                                    key={idx}
                                                    className="tag-item text-xs px-2 py-1 text-white rounded-full backdrop-blur-sm"
                                                    style={{
                                                        transitionDelay: `${
                                                            idx * 0.05
                                                        }s`,
                                                    }}
                                                >
                                                    {tag}
                                                </span>
                                            ))}
                                        {project.tags.length > 2 && (
                                            <span
                                                className="tag-item text-xs px-2 py-1 text-white rounded-full backdrop-blur-sm"
                                                style={{
                                                    transitionDelay: "0.1s",
                                                }}
                                            >
                                                +{project.tags.length - 2}
                                            </span>
                                        )}
                                    </div>

                                    {/* CTA */}
                                    <div className="cta text-right">
                                        <span className="inline-flex items-center text-white text-sm font-medium">
                                            Voir le projet
                                            <svg
                                                className="arrow-icon ml-2 w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth="2"
                                                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                                                ></path>
                                            </svg>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </button>
    );
};

export default ProjectItem;
